import React, {FC} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface ChipProps {
  id: string | number;
  text: string;
  onRemove?: (id: string | number) => void;
  color?: 'default' | 'primary' | 'secondary' | 'error';
  size?: 'small' | 'medium';
}

const Chip: FC<ChipProps> = ({
  id,
  text,
  onRemove,
  color = 'default',
  size = 'medium',
}) => {
  const handleRemove = () => {
    onRemove?.(id);
  };

  const colors = {
    default: {
      bg: '#F5F5F5',
      text: '#424242',
      hover: 'rgba(0, 0, 0, 0.04)',
    },
    primary: {
      bg: '#E3F2FD',
      text: '#1976D2',
      hover: 'rgba(25, 118, 210, 0.04)',
    },
    secondary: {
      bg: '#F3E5F5',
      text: '#9C27B0',
      hover: 'rgba(156, 39, 176, 0.04)',
    },
    error: {
      bg: '#FFEBEE',
      text: '#D32F2F',
      hover: 'rgba(211, 47, 47, 0.04)',
    },
  };

  const sizes = {
    small: {
      py: 0.25,
      pl: 1.5,
      pr: 0.5,
      fontSize: '12px',
      iconSize: 14,
    },
    medium: {
      py: 0.5,
      pl: 2,
      pr: 1,
      fontSize: '14px',
      iconSize: 16,
    },
  };

  const currentColor = colors[color];
  const currentSize = sizes[size];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: currentColor.bg,
        borderRadius: '16px',
        py: currentSize.py,
        pl: currentSize.pl,
        pr: currentSize.pr,
        m: 0.5,
        maxWidth: '100%',
      }}
    >
      <Typography
        sx={{
          fontSize: currentSize.fontSize,
          color: currentColor.text,
          mr: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Typography>
      {onRemove && (
        <IconButton
          size="small"
          onClick={handleRemove}
          sx={{
            p: '2px',
            '&:hover': {
              bgcolor: currentColor.hover,
            },
          }}
        >
          <CloseIcon
            sx={{fontSize: currentSize.iconSize, color: currentColor.text}}
          />
        </IconButton>
      )}
    </Box>
  );
};

export default Chip;
